import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BackgroundDecoration from '@/components/ui/BackgroundDecoration';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import BookCard from '@/features/library/components/BookCard';
import { useSearchBooks } from '@/features/library/hooks/useSearchBooks';
import { analyticsService } from '@/services/analyticsService';
import { useMySections } from '@/features/chapters/hooks/useMySections';
import { useTimelineEvents } from '@/features/learning/hooks/useTimelineEvents';
import { useCharacterAnalysis } from '@/features/learning/hooks/useCharacterAnalysis';
import { useCharacterRelationships } from '@/features/learning/hooks/useCharacterRelationships';
import { useBookCharacters } from '@/features/library/hooks/useBookCharacters';

const OnboardingBooks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateTopic = (location.state as any)?.topicName as string | undefined;
  const queryTopic = new URLSearchParams(location.search).get('topic') || undefined;
  const savedTopic = sessionStorage.getItem('onboarding_topic_name') || undefined;
  const topicName = stateTopic || queryTopic || savedTopic;
  
  const [allBooks, setAllBooks] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [launchingBookId, setLaunchingBookId] = useState<number | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data: learningData, isLoading: learningLoading, error: learningError } = useMySections(
    launchingBookId ?? undefined,
    { enabled: launchingBookId !== null },
  );
  const chapters = learningData?.chapters ?? [];
  const firstChapter = useMemo(
    () => chapters.find((chapter) => chapter.sections?.some((section) => !section.is_done)) ?? chapters[0],
    [chapters],
  );
  const firstSection = useMemo(
    () => firstChapter?.sections?.find((section) => !section.is_done) ?? firstChapter?.sections?.[0],
    [firstChapter],
  );
  const needsNovelFeatures = firstSection?.section_type === 'novel';
  const timelineQuery = useTimelineEvents(launchingBookId ?? undefined, { enabled: needsNovelFeatures });
  const analysisQuery = useCharacterAnalysis(launchingBookId ?? undefined, { enabled: needsNovelFeatures });
  const relationshipsQuery = useCharacterRelationships(launchingBookId ?? undefined, { enabled: needsNovelFeatures });
  const charactersQuery = useBookCharacters(launchingBookId, { enabled: needsNovelFeatures });

  const { data, isLoading, isFetching } = useSearchBooks(
    { topic: topicName, page: currentPage, limit: 10 },
    { enabled: !!topicName }
  );

  // جمع‌آوری کتاب‌ها از صفحات مختلف
  useEffect(() => {
    if (data?.books) {
      // Keep pagination independent from cover decoding. The observer must be
      // released as soon as the API page arrives, otherwise image loading can
      // make the next page look like it never loaded.
      if (currentPage === 1) {
        setAllBooks(data.books);
      } else {
        setAllBooks(prev => [...prev, ...data.books]);
      }
      
      // اطلاعات صفحه‌بندی در پاسخ API داخل pagination قرار دارد.
      setTotalPages(data.pagination?.total_pages ?? 1);
      setIsLoadingMore(false);
    }
  }, [data, currentPage]);

  // بارگذاری صفحه بعدی فقط وقتی کاربر به انتهای لیست اسکرول می‌کند.
  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement || currentPage >= totalPages) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || isLoading || isFetching || isLoadingMore) return;
        setIsLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      },
      { rootMargin: '240px 0px' },
    );

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [currentPage, totalPages, isLoading, isFetching, isLoadingMore]);

  useEffect(() => {
    if (launchingBookId === null || !firstChapter || !firstSection) return;
    if (needsNovelFeatures && !(timelineQuery.isFetched && analysisQuery.isFetched && relationshipsQuery.isFetched && charactersQuery.isFetched)) return;

    const route = firstSection.section_type === 'novel' ? '/onboarding/novel-read' : '/onboarding/read';
    const analysisCharacters = analysisQuery.data?.data?.characters ?? [];
    const relationshipCharacters = relationshipsQuery.data?.data?.characters ?? [];
    navigate(route, {
      replace: true,
      state: {
        bookId: launchingBookId,
        bookTitle: learningData?.title,
        chapterId: firstChapter.chapter_id,
        sectionId: firstSection.section_id,
        sectionTitle: firstSection.title,
        summary: firstSection.summary,
        previous_section_summary: firstSection.previous_section_summary,
        trailer: firstSection.trailer,
        content: firstSection.content,
        returnTo: '/onboarding/books',
        onboardingPreview: true,
        onboardingFeatureFlags: needsNovelFeatures ? {
          hasChatCharacters: (charactersQuery.data?.characters?.length ?? 0) > 0,
          hasCharacterInsights: analysisCharacters.some((character) => {
            const relationshipCharacter = relationshipCharacters.find(
              (item) => item.character_name === character.character_name,
            );
            return (character.analyses?.length ?? 0) > 0 && (relationshipCharacter?.relationships?.length ?? 0) > 0;
          }),
          // With spoiler protection enabled, locked sections intentionally omit
          // their events from the response. The presence of a section is still
          // enough to know that this book has timeline data.
          hasTimeline: (timelineQuery.data?.data?.sections?.length ?? 0) > 0,
        } : undefined,
      },
    });
  }, [analysisQuery.data, analysisQuery.isFetched, charactersQuery.data, charactersQuery.isFetched, firstChapter, firstSection, launchingBookId, learningData?.title, navigate, needsNovelFeatures, relationshipsQuery.data, relationshipsQuery.isFetched, timelineQuery.data, timelineQuery.isFetched]);

  const books = useMemo(() => allBooks, [allBooks]);

  if (launchingBookId !== null) {
    if (learningLoading || (needsNovelFeatures && !(
      timelineQuery.isFetched &&
      analysisQuery.isFetched &&
      relationshipsQuery.isFetched &&
      charactersQuery.isFetched
    ))) {
      return <div className="min-h-screen flex items-center justify-center bg-white"><LoadingSpinner /></div>;
    }

    if (learningError || !firstChapter || !firstSection) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white px-6" dir="rtl">
          <ErrorDisplay message={learningError?.message || 'اولین بخش کتاب پیدا نشد.'} onRetry={() => setLaunchingBookId(null)} />
        </div>
      );
    }
    return <div className="min-h-screen flex items-center justify-center bg-white"><LoadingSpinner /></div>;
  }

  return (
    <div
      className="min-h-screen bg-white flex flex-col px-7 pb-12 relative overflow-x-hidden"
      style={{ paddingTop: 'calc(var(--onboarding-header-h, 110px) + 10px)' }}
      dir="rtl"
    >
      {isLoading && currentPage === 1 ? (
        <div className="flex-1 flex items-center justify-center pt-2">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-x-5 gap-y-6 pt-2 pb-24">
            {books.map((book, index) => (
              <div
                key={book.book_id}
                className="onboarding-book-enter"
                style={{ animationDelay: `${index * 45}ms` }}
              >
                <BookCard
                  book={book}
                  onClick={() => {
                    localStorage.setItem('onboarding_book_id', String(book.book_id));
                    sessionStorage.setItem('onboarding_book_metadata', JSON.stringify({
                      book_id: book.book_id,
                      book_title: book.title,
                      book_category: book.topic || 'unknown',
                      content_type: book.content_type || 'unknown',
                    }));
                    void analyticsService.logEvent('onboarding_book_selected', {
                      book_id: String(book.book_id),
                      book_title: book.title,
                      book_category: book.topic || 'unknown',
                      content_type: book.content_type || 'unknown',
                    });
                    setLaunchingBookId(Number(book.book_id));
                  }}
                />
              </div>
            ))}
          </div>

          <div ref={loadMoreRef} className="h-1 w-full" aria-hidden="true" />
          
          {/* نمایش loading برای صفحات بعدی */}
          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          )}
        </>
      )}

      <BackgroundDecoration />
    </div>
  );
};

export default OnboardingBooks;
